import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Rocket, Shield, Cpu, Mail, Phone, Gamepad2, Terminal } from 'lucide-react';
import './v2.css';

function HomeV2() {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const yOffset = useTransform(scrollYProgress, [0, 0.5], [0, -100]);
  const canvasRef = useRef(null);

  // Pixel Particle animation setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 120; // More particles for pixel dust look

    class PixelParticle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        // Blocky sizes: 2, 4, or 6 pixels
        this.size = Math.floor(Math.random() * 3 + 1) * 2;
        this.speedX = (Math.random() - 0.5) * 1.5;
        this.speedY = (Math.random() - 0.5) * 1.5;
        this.opacity = Math.random() * 0.6 + 0.2;
        // Cyan and Magenta colors
        this.color = Math.random() > 0.5 ? '0, 255, 255' : '255, 0, 255';
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        // Pixel block instead of circle
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fillRect(Math.floor(this.x), Math.floor(this.y), this.size, this.size);
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new PixelParticle());
    }

    // Grid lines connect pixels like circuit paths (horizontal/vertical only)
    function connectParticles() {
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.abs(dx) + Math.abs(dy); // Manhattan distance for grid feel

          if (distance < 100) {
            ctx.strokeStyle = `rgba(100, 200, 255, ${0.1 * (1 - distance / 100)})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            // Right angle path instead of direct line
            ctx.lineTo(particles[j].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    }

    function animate() {
      // Clear with slight trailing
      ctx.fillStyle = 'rgba(5, 5, 12, 0.3)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      connectParticles();
      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: <Terminal className="w-8 h-8" />,
      title: "FUTURE-READY",
      description: "State-of-the-art architecture designed to scale and defeat the final boss.",
      colorClass: "pixel-box-cyan",
      textColor: "text-cyan-400"
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "INTEGRATION",
      description: "Seamlessly connecting all systems together like a neural network.",
      colorClass: "pixel-box-purple",
      textColor: "text-purple-400"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "CYBER SECURITY",
      description: "Maximum level armor to protect your most valuable data items.",
      colorClass: "pixel-box-cyan",
      textColor: "text-cyan-400"
    }
  ];

  return (
    <div className="v2-root relative min-h-screen bg-[#05050C] text-white overflow-hidden selection:bg-fuchsia-500/50">
      
      {/* Dynamic Background Canvas */}
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
      />
      
      {/* V2 Specific Ambient Light Orbs for Tech feeling */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
        <div className="absolute w-[60vw] h-[60vw] rounded-full blur-[120px] bg-[radial-gradient(circle,rgba(0,255,255,0.06)_0%,transparent_60%)] -left-[10%] -top-[10%] animate-[pulse_10s_ease-in-out_infinite]" />
        <div className="absolute w-[50vw] h-[50vw] rounded-full blur-[120px] bg-[radial-gradient(circle,rgba(255,0,255,0.05)_0%,transparent_60%)] -right-[10%] bottom-[10%] animate-[pulse_12s_ease-in-out_infinite]" />
      </div>

      {/* Grid overlay & Scanlines for CRT feel */}
      <div className="fixed inset-0 pointer-events-none grid-overlay" style={{ zIndex: 2 }}></div>
      <div className="fixed inset-0 pointer-events-none v2-scanlines" style={{ zIndex: 3 }}></div>

      {/* Floating HUD Elements */}
      <div className="fixed top-6 right-8 text-sm font-terminal text-fuchsia-500 pointer-events-none hidden md:block" style={{ zIndex: 10 }}>
        [ SCORE: 999990 ]<br/>
        [ LEVEL: MAX   ]
      </div>
      
      <div className="fixed bottom-6 left-8 flex gap-3 pointer-events-none" style={{ zIndex: 10 }}>
        {[1, 2, 3].map((i) => (
          <div 
            key={i}
            className={`w-3 h-3 ${i === 1 ? 'bg-cyan-500' : 'bg-transparent border-2 border-cyan-500'}`}
          />
        ))}
      </div>

      <div className="relative z-10">
        
        {/* HERO SECTION */}
        <motion.section 
          className="min-h-screen flex flex-col items-center justify-center px-4 md:px-8 relative w-full"
          style={{ opacity, y: yOffset }}
        >
          <div className="max-w-7xl mx-auto text-center w-full mt-[-10vh]">
            
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.5, type: "spring", bounce: 0.5 }}
              className="relative inline-block mb-10"
            >
              <h1 className="text-[clamp(1.5rem,6.5vw,6rem)] whitespace-nowrap font-pixel pixel-glow-text text-white tracking-widest leading-normal px-4">
                RAISE YOU UP
              </h1>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-16"
            >
              <h2 className="relative inline-block text-[clamp(0.8rem,3vw,1.5rem)] font-terminal text-cyan-400 tracking-[0.2em] uppercase mb-4">
                <span className="absolute -left-5 md:-left-8 opacity-70">{'>'}</span> NEXT-GEN SOFTWARE SOLUTIONS<span className="animate-pulse">_</span>
              </h2>
              <p className="max-w-2xl mx-auto text-gray-300 font-terminal text-lg sm:text-2xl leading-relaxed">
                Elevate your business to the future. Clear the stages of your competitors with boss-level software architecture!
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <a href="#services" className="inline-block px-8 py-4 font-pixel text-sm md:text-base pixel-btn-cyan arcade-blink">
                <Gamepad2 className="w-5 h-5 inline-block mr-3 -mt-1" />
                PRESS START
              </a>
            </motion.div>

          </div>
          
          {/* INSERT COIN INDICATOR */}
          <motion.div 
            className="absolute bottom-16 left-1/2 -translate-x-1/2 font-pixel text-[10px] text-fuchsia-500 tracking-[0.3em]"
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 1, repeat: Infinity, stepEnd: true }}
          >
            INSERT COIN TO SCROLL
          </motion.div>
        </motion.section>

        {/* SERVICES SECTION */}
        <section id="services" className="min-h-screen pt-32 pb-24 flex items-center relative z-20">
          <div className="max-w-7xl mx-auto px-6 w-full">
            
            <div className="mb-16 text-center">
              <div className="text-fuchsia-500 font-terminal text-2xl tracking-[0.2em] mb-4">
                STAGE 1
              </div>
              <h2 className="text-3xl md:text-5xl font-pixel glow-purple text-white mb-6">
                OUR EXPERTISE
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 pl-2 pr-2">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.4, delay: index * 0.2 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className={`relative p-8 ${service.colorClass} transition-transform duration-200`}
                >
                  <div className={`mb-6 ${service.textColor}`}>
                    {service.icon}
                  </div>
                  
                  <h3 className={`text-base md:text-lg lg:text-xl font-pixel mb-4 ${service.textColor} leading-normal`}>
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-300 font-terminal text-xl leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT SECTION */}
        <section id="contact" className="min-h-screen py-24 flex items-center relative z-20">
          <div className="max-w-6xl mx-auto px-6 w-full text-center">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="mb-16"
            >
              <div className="text-cyan-400 font-terminal text-2xl tracking-[0.2em] mb-4">
                FINAL STAGE
              </div>
              <h2 className="text-[clamp(1.2rem,4vw,3rem)] font-pixel glow-cyan text-white mb-6">
                READY TO UPGRADE?
              </h2>
              <p className="font-terminal text-2xl text-gray-400 max-w-2xl mx-auto">
                Save your game state and power up! Let us be your partner in clearing all business obstacles.
              </p>
            </motion.div>

            <div className="flex flex-col md:flex-row justify-center gap-10 max-w-4xl mx-auto">
              
              {/* Phone CTA */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="flex-1"
              >
                 <a href="tel:020709989" className="block w-full px-6 py-10 font-pixel text-center pixel-btn-cyan">
                    <Phone className="w-12 h-12 mx-auto mb-6 text-cyan-400" />
                    <div className="text-[10px] text-cyan-200 tracking-widest mb-4">CALL SUPPORT</div>
                    <div className="text-xl md:text-2xl mb-2 text-white">02-070-9989</div>
                 </a>
              </motion.div>

              {/* Email CTA */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="flex-1"
              >
                 <a href="mailto:teerasak.d@raiseyouup.co.th" className="block w-full px-6 py-10 font-pixel text-center pixel-btn-purple">
                    <Mail className="w-12 h-12 mx-auto mb-6 text-fuchsia-400" />
                    <div className="text-[10px] text-fuchsia-200 tracking-widest mb-4">SEND MESSAGE</div>
                    <div className="text-sm md:text-base mb-2 text-white overflow-hidden text-ellipsis">EMAIL US</div>
                 </a>
              </motion.div>

            </div>

             {/* Footer */}
             <div className="mt-32 pt-8 flex flex-col items-center gap-4 text-xs font-terminal text-gray-500 tracking-widest uppercase">
               <div>© 2026 RAISE YOU UP SOLUTION. LEVEL COMPLETED.</div>
               <div className="flex gap-4">
                 <span className="hover:text-cyan-400 cursor-pointer border-b border-transparent hover:border-cyan-400">CREDITS</span>
                 <span className="hover:text-fuchsia-400 cursor-pointer border-b border-transparent hover:border-fuchsia-400">SAVE STATE</span>
               </div>
             </div>
             
          </div>
        </section>

      </div>
    </div>
  );
}

export default HomeV2;
